import axios from 'axios';
import {
  clearTokens,
  getAccessToken,
  setAccessToken,
} from './tokenStorage';

const API_BASE = '/api';
let refreshPromise = null;

let onAuthFailure = () => {
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};
export const setAuthFailureCallback = (cb) => { onAuthFailure = cb; };

// Create axios instance with default config.
// withCredentials sends the HttpOnly refreshToken cookie on cross-origin requests under /api/auth.
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const refreshApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const extractResponseData = (payload) => payload?.data ?? payload;

const refreshAccessToken = async () => {
  if (!refreshPromise) {
    // No body needed — the refresh token rides in the HttpOnly cookie set on login.
    refreshPromise = refreshApi
      .post('/auth/refresh', {})
      .then((response) => {
        const data = extractResponseData(response.data);
        const accessToken = data?.accessToken ?? data?.access_token;

        if (!accessToken) {
          throw new Error('Refresh response did not include an access token');
        }

        setAccessToken(accessToken);
        return accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // If sending FormData, remove Content-Type to let axios set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Unwrap DataResponse { data: <content> } from backend
api.interceptors.response.use(
  (response) => {
    if (response.data && response.data.data !== undefined) {
      response.data = response.data.data;
    }
    return response;
  },
);

// Handle token refresh on 401/403
// When access token expires, call POST /api/auth/refresh to get a new one.
// Only treat the session as dead if the refresh endpoint itself returns 401 —
// transient failures (network error, server 5xx, request abort during browser
// back/forward) are NOT logout signals. Bouncing to /login on those used to
// kick the user out after pressing the back button on Firefox.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || '';
    const isAuthRequest = typeof requestUrl === 'string' && requestUrl.includes('/auth/');

    // Handle both 401 (Unauthorized) and 403 (Forbidden) for token expiration
    if ((status === 401 || status === 403) && originalRequest && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();

        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${accessToken}`,
        };

        return api(originalRequest);
      } catch (refreshError) {
        // Only log the user out if the refresh endpoint itself definitively
        // rejected the cookie (401). Anything else — server hiccup, network
        // blip, request aborted because the page bfcache-restored mid-flight —
        // bubbles up to the caller without touching the session.
        const refreshStatus = refreshError?.response?.status;
        if (refreshStatus === 401) {
          clearTokens();
          onAuthFailure();
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: (email, password) => 
    api.post('/auth/login', { email, password }),
  
  register: (email, name, password, phone = null, afterLoginUrl = null) =>
    api.post(
      '/auth/register',
      { email, name, password, phone },
      afterLoginUrl
        ? {
            params: {
              after_login_url: afterLoginUrl,
            },
          }
        : undefined
    ),
  
  logout: () => {
    clearTokens();
    window.location.href = '/login';
  },
  
  getCurrentUser: () => 
    api.get('/users/me'),
  
  getUserCommunities: () =>
    api.get('/users/me/communities'),
  
  // OAuth methods
  getGoogleAuthUrl: (afterLoginUrl = null) =>
    api.get('/auth/google', {
      params: {
        redirect_uri: window.location.origin + '/oauth-callback.html',
        ...(afterLoginUrl ? { after_login_url: afterLoginUrl, origin: window.location.origin } : {}),
      }
    }),
  
  linkGoogleAccount: () =>
    api.get('/auth/google/link', {
      params: {
        redirect_uri: window.location.origin + '/oauth-callback.html'
      }
    }),
  
  handleGoogleCallback: (code, state) =>
    api.post('/auth/google/token', {
      code,
      state,
      redirectUri: window.location.origin + '/oauth-callback.html'
    }),
};

export const invitationService = {
  getByToken: (token) =>
    api.get(`/invitations/token/${token}`),

  getPending: () =>
    api.get('/invitations/pending'),


  acceptByToken: (token) =>
    api.post(`/invitations/token/${token}/accept`),

  rejectByToken: (token) =>
    api.post(`/invitations/token/${token}/reject`),
};

export const joinService = {
  getByToken: (token) =>
    api.get(`/join/${token}`),

  // Returns raw axios response so callers can read .status (201 = joined, 202 = pending approval)
  joinByToken: (token) =>
    api.post(`/join/${token}`, {}, { validateStatus: (s) => s === 200 || s === 201 || s === 202 }),
};

// User services
export const userService = {
  getMe: () =>
    api.get('/users/me'),
  
  getMyCommunities: () =>
    api.get('/users/me/communities'),

  getMyJoinRequests: () =>
    api.get('/users/me/join-requests'),

  getModerationContext: (userId) =>
    api.get(`/users/${userId}/moderation-context`),

  getMyEmails: () =>
    api.get('/users/me/emails'),
  
  updateProfile: (data) =>
    api.put('/users/me', data),
  
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    // Don't set Content-Type manually - let axios set it with the boundary
    return api.post('/users/me/profile-picture', formData);
  },
  
  getUserById: (id) =>
    api.get(`/users/${id}`),
  
  getUserName: (id) =>
    api.get(`/users/${id}/name`),

  getUserCommunityCount: (id) =>
    api.get(`/users/${id}/community-count`),
  
  addEmail: (email) =>
    api.post('/users/me/emails', { email }),
  
  setPrimaryEmail: (emailId) =>
    api.put(`/users/me/emails/${emailId}/primary`),
  
  deleteEmail: (emailId) =>
    api.delete(`/users/me/emails/${emailId}`),
  
  resendVerificationEmail: (emailId) =>
    api.post(`/users/me/emails/${emailId}/resend-verification`),

  changePassword: (currentPassword, newPassword) =>
    api.put('/users/me/password', { currentPassword, newPassword }),

  updateProfileImage: (formData) =>
    // Don't set Content-Type manually - let axios set it with the boundary
    api.post('/users/me/profile-image', formData),
};

// Product services
export const productService = {
  getProducts: (params = {}) => 
    api.get('/products', { params }),

  getProductsByCreator: (creatorId) =>
    api.get('/products', { params: { creatorId } }),

  getProductsByCreatorCursor: ({ creatorId, pageSize = 16, cursor } = {}) =>
    api.get(`/products/creator/${creatorId}`, {
      params: {
        pageSize,
        ...(cursor ? { cursor } : {}),
      },
    }),
  
  getProductById: (id) => 
    api.get(`/products/${id}`),
  
  getProductsByCommunity: (communityId) => 
    api.get('/products', { params: { communityId } }),
  
  getCommunityProductCards: (communityId) =>
    api.get(`/products/community/${communityId}`),
  
  createProduct: (data) => 
    api.post('/products', data),
  
  updateProduct: (id, data) => 
    api.put(`/products/${id}`, data),
  
  deleteProduct: (id) => 
    api.delete(`/products/${id}`),
  
  createProductWithImages: (formData) =>
    // Don't set Content-Type manually - let axios set it with the boundary
    api.post('/products/with-image', formData),
  
  getRecentlyViewed: (limit = 20) =>
    api.get('/users/me/recently-viewed', { params: { limit } }),
  
  getProductFeed: (limit = 20) =>
    api.get('/products/feed', { params: { limit } }),
  
  getProductCards: ({ pageSize = 20, cursor, q, categoryId, communityIds, sortBy } = {}) =>
    api.get('/products/cards', {
      params: {
        pageSize,
        ...(cursor ? { cursor } : {}),
        ...(q ? { q } : {}),
        ...(categoryId ? { categoryId } : {}),
        ...(communityIds?.length ? { communityIds: communityIds.join(',') } : {}),
        ...(sortBy && sortBy !== 'RECENT' ? { sortBy } : {}),
      },
    }),
  
  // Multiple image management
  addProductImage: (productId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    // Don't set Content-Type manually - let axios set it with the boundary
    return api.post(`/products/${productId}/images`, formData);
  },
  
  getProductImages: (productId) =>
    api.get(`/products/${productId}/images`),
  
  deleteProductImage: (productId, imageId) =>
    api.delete(`/products/${productId}/images/${imageId}`),
  
  reorderProductImages: (productId, imageIds) =>
    api.put(`/products/${productId}/images/reorder`, { imageIds }),
  
  setPrimaryImage: (productId, imageId) =>
    api.put(`/products/${productId}/images/${imageId}/primary`),
};

// Community services
export const communityService = {
  getCommunities: (params = {}) => 
    api.get('/communities', { params }),
  
  getCommunityById: (id) => 
    api.get(`/communities/${id}`),
  
  getUserCommunities: () => 
    api.get('/users/me/communities'),
  
  createCommunity: (data) => 
    api.post('/communities', data),
  
  updateCommunity: (id, data) => 
    api.put(`/communities/${id}`, data),

  updateAllowDomainJoin: (id, allowDomainJoin) =>
    api.put(`/communities/${id}/allow-domain-join`, { allowDomainJoin }),

  uploadCommunityImage: (id, file) => {
    const formData = new FormData();
    formData.append('image', file);
    // Don't set Content-Type manually - let axios set it with the boundary
    return api.post(`/communities/${id}/image`, formData);
  },
  
  getCommunityMembers: (id, { pageSize, cursor } = {}) =>
    api.get(`/communities/${id}/users`, {
      params: {
        ...(pageSize ? { pageSize } : {}),
        ...(cursor ? { cursor } : {}),
      },
    }),
  
  checkMembership: (communityId, userId) =>
    api.get(`/communities/${communityId}/users/${userId}`),
  
  joinCommunity: (communityId, userId) =>
    api.post(`/communities/${communityId}/users`, { userId }),
  
  leaveCommunity: (communityId, userId) =>
    api.delete(`/communities/${communityId}/users/${userId}`),
  
  createInvitation: (communityId, email, message = null) =>
    api.post(`/communities/${communityId}/invitations`, { email, message }),
  
  createJoinLink: (communityId, { maxUses, expiresAt, requiresApproval } = {}) =>
    api.post(`/communities/${communityId}/join-links`, {
      ...(maxUses != null ? { maxUses } : {}),
      ...(expiresAt != null ? { expiresAt } : {}),
      ...(requiresApproval != null ? { requiresApproval } : {}),
    }),

  setInvitePolicy: (id, invitePolicy) =>
    api.put(`/communities/${id}`, { invitePolicy }),
  
  getJoinLinks: (communityId) =>
    api.get(`/communities/${communityId}/join-links`),
  
  deactivateJoinLink: (linkId) =>
    api.delete(`/communities/join-links/${linkId}`),
  
  getAvailableToJoin: () =>
    api.get('/communities/available-to-join'),

  // Admin / approval endpoints
  setLinkJoinApproval: (id, requireApproval) =>
    api.put(`/communities/${id}/link-join-approval`, { requireApproval }),

  getAdmins: (id) =>
    api.get(`/communities/${id}/admins`),

  promoteAdmin: (id, userId) =>
    api.post(`/communities/${id}/admins/${userId}`),

  revokeAdmin: (id, userId) =>
    api.delete(`/communities/${id}/admins/${userId}`),

  getJoinRequests: (id, { status = 'PENDING', page, size } = {}) =>
    api.get(`/communities/${id}/join-requests`, { params: { status, page, size } }),

  approveJoinRequest: (id, reqId) =>
    api.post(`/communities/${id}/join-requests/${reqId}/approve`),

  rejectJoinRequest: (id, reqId) =>
    api.post(`/communities/${id}/join-requests/${reqId}/reject`),

  cancelMyJoinRequest: (id, reqId) =>
    api.delete(`/communities/${id}/join-requests/${reqId}`),

  getBlockedUsers: (id) =>
    api.get(`/communities/${id}/blocks`),

  blockUser: (id, userId, reason = null) =>
    api.post(`/communities/${id}/blocks/${userId}`, { reason }),

  unblockUser: (id, userId) =>
    api.delete(`/communities/${id}/blocks/${userId}`),

  removeUser: (communityId, userId) =>
    api.delete(`/communities/${communityId}/users/${userId}`),

  deactivateAllJoinLinks: (id) =>
    api.delete(`/communities/${id}/join-links`),

  // ── Sedes (admin) y Schedules (member) ────────────────────────────
  // sedes: array de strings, max 8, max 32 chars c/u (validado en server).
  updateSedes: (communityId, sedes) =>
    api.put(`/communities/${communityId}/sedes`, { sedes }),
  // toggle del feature de horarios para toda la comunidad.
  updateScheduleEnabled: (communityId, enabled) =>
    api.put(`/communities/${communityId}/schedule-enabled`, { enabled }),
  // contexto del editor: sedes + scheduleEnabled + mi schedule (puede ser null).
  getMyScheduleContext: (communityId) =>
    api.get(`/communities/${communityId}/schedule/me`),
  // body: { schedule: { days: [[{start,end,sede}, ...], ...] } }; al PUT
  // el server compacta + valida y devuelve el mismo shape post-update.
  updateMySchedule: (communityId, schedule) =>
    api.put(`/communities/${communityId}/schedule/me`, { schedule }),
  // schedule de otro user en una comunidad puntual (raro de usar — la
  // mayor parte de las vistas pide el agregado en producto/álbum).
  getUserScheduleInCommunity: (communityId, userId) =>
    api.get(`/communities/${communityId}/schedule/${userId}`),
  // Lista de comunidades en las que un usuario tiene horarios y que el
  // viewer comparte. Devuelve solo metadata (id, nombre, sedes); el
  // schedule se trae aparte. Usado en /profile y /users/{id}.
  listSchedulesByUser: (userId) =>
    api.get(`/communities/schedules/by-user/${userId}`),
};

// Reservation services
export const reservationService = {
  createReservation: (productId, message) =>
    api.post('/reservations', { productId, message }),
  
  getMyReservations: () => 
    api.get('/reservations'),
  
  // For now, we'll use the same endpoint since the backend doesn't have a separate one
  // The backend would need to implement filtering based on the authenticated user's products
  getReservationsOnMyProducts: () => 
    api.get('/reservations'), // This will get all reservations for now
  
  withdrawReservation: (id) => 
    api.put(`/reservations/${id}`, { status: 'WITHDRAWN' }),
  
  acceptReservation: (id) => 
    api.put(`/reservations/${id}`, { status: 'ACCEPTED' }),
  
  rejectReservation: (id) => 
    api.put(`/reservations/${id}`, { status: 'REJECTED' }),
  
  getReservationById: (id) =>
    api.get(`/reservations/${id}`),

  getSellerContact: (reservationId) =>
    api.get(`/reservations/${reservationId}/contact`, {
      validateStatus: s => s === 200 || s === 404 || s === 403,
    }),

  getCounterpartyContact: (reservationId) =>
    api.get(`/reservations/${reservationId}/counterparty-contact`, {
      validateStatus: s => s === 200 || s === 404 || s === 403,
    }),

  getMyReservationsCursor: ({ pageSize = 10, cursor } = {}) =>
    api.get('/reservations', {
      params: {
        pageSize,
        ...(cursor ? { cursor } : {}),
      },
    }),

  getPurchasesCursor: ({ pageSize = 20, cursor } = {}) =>
    api.get('/reservations/purchases', {
      params: {
        pageSize,
        ...(cursor ? { cursor } : {}),
      },
    }),

  getSalesCursor: ({ pageSize = 20, cursor } = {}) =>
    api.get('/reservations/sales', {
      params: {
        pageSize,
        ...(cursor ? { cursor } : {}),
      },
    }),
};

// Wishlist services
export const wishlistService = {
  getWishlist: () =>
    api.get('/wishlist'),

  getWishlistCursor: ({ pageSize = 16, cursor } = {}) =>
    api.get('/wishlist', {
      params: {
        pageSize,
        ...(cursor ? { cursor } : {}),
      },
    }),

  getWishlistWithStatus: () =>
    api.get('/wishlist/status'),
  
  addToWishlist: (productId) => 
    api.post(`/wishlist/products/${productId}`),
  
  removeFromWishlist: (productId) => 
    api.delete(`/wishlist/products/${productId}`),

  toggleWishlist: (productId) =>
    api.put(`/wishlist/products/${productId}/toggle`),
  
  checkWishlistStatus: (productId) =>
    api.get(`/wishlist/products/${productId}`),
};

// Search services
export const searchService = {
  searchProducts: (query, filters = {}) => 
    api.get('/search/products', { params: { q: query, ...filters } }),
  
  getSearchSuggestions: (query) => 
    api.get('/search/suggestions', { params: { q: query } }),
};

export const categoryService = {
  getCategories: (active = true) =>
    api.get('/categories', { params: { active } }),
};

export const tagService = {
  updateProductTags: (productId, tags) =>
    api.put(`/tags/products/${productId}`, { tags }),
};

// Notification services
export const notificationService = {
  getNotifications: () => 
    api.get('/notifications'),
  
  getUnreadCount: () => 
    api.get('/notifications/unread/count'),
  
  markAsRead: (id) => 
    api.put(`/notifications/${id}/read`),
  
  markAllAsRead: () => 
    api.put('/notifications/read-all'),
  
  deleteNotification: (id) =>
    api.delete(`/notifications/${id}`),
};

// Album / Sticker services
export const albumService = {
  getMyAlbum: () =>
    api.get('/album/me'),

  updateMyAlbum: (base64Data) =>
    api.put('/album/me', { data: base64Data }),

  addStickers: (stickerNumbers) =>
    api.post('/album/stickers/add', { stickers: stickerNumbers }),

  removeStickers: (stickerNumbers) =>
    api.post('/album/stickers/remove', { stickers: stickerNumbers }),

  getUserAlbum: (userId, extraHeaders = {}) =>
    api.get(`/album/users/${userId}`, {
      headers: extraHeaders,
      validateStatus: s => s === 200 || s === 304,
    }),

  getBulkAlbums: (communityIds) => {
    const params = {};
    if (Array.isArray(communityIds) && communityIds.length > 0) {
      params.communityIds = communityIds.join(',');
    }
    return api.get('/album/bulk', { params });
  },

  proposeTrade: (receiverId, offered, requested) =>
    api.post('/album/trades', { receiverId, offered, requested }),

  getIncomingTrades: () =>
    api.get('/album/trades/incoming'),

  getOutgoingTrades: () =>
    api.get('/album/trades/outgoing'),

  acceptTrade: (tradeId) =>
    api.put(`/album/trades/${tradeId}`, { status: 'ACCEPTED' }),

  completeTrade: (tradeId) =>
    api.put(`/album/trades/${tradeId}`, { status: 'COMPLETED' }),

  rejectTrade: (tradeId) =>
    api.put(`/album/trades/${tradeId}`, { status: 'REJECTED' }),

  cancelTrade: (tradeId) =>
    api.put(`/album/trades/${tradeId}`, { status: 'CANCELLED' }),

  getActiveTrades: () =>
    api.get('/album/trades/active'),

  getTradeHistory: () =>
    api.get('/album/trades/history'),

  getMyLocks: () =>
    api.get('/album/locks/me'),

  getUserLocks: (userId) =>
    api.get(`/album/locks/users/${userId}`),

  // ── Public offers ──────────────────────────────────────────────
  createOffer: (offered, requested) =>
    api.post('/album/offers', { offered, requested }),

  listOffers: (filter = 'matching') =>
    api.get('/album/offers', { params: { filter } }),

  // Browse paginado server-side: search (número de figurita o nombre del
  // publisher), filtro por comunidad, sort y paginación. Devuelve
  // { items, total, limit, offset, hasMore }.
  browseOffers: ({ q, communityId, sortBy, limit, offset, excludeMine } = {}) => {
    const params = {};
    if (q != null && q !== '') params.q = q;
    if (communityId != null) params.communityId = communityId;
    if (sortBy) params.sortBy = sortBy;
    if (limit != null) params.limit = limit;
    if (offset != null) params.offset = offset;
    if (excludeMine != null) params.excludeMine = excludeMine;
    return api.get('/album/offers/browse', { params });
  },

  getOffer: (offerId) =>
    api.get(`/album/offers/${offerId}`),

  claimOffer: (offerId) =>
    api.post(`/album/offers/${offerId}/claim`),

  cancelOffer: (offerId) =>
    api.delete(`/album/offers/${offerId}`),

  // ── Valuations + Quick swap ────────────────────────────────────
  getMyValuations: () =>
    api.get('/album/me/valuations'),

  setMyValuations: (values) =>
    api.put('/album/me/valuations', { values }),

  getUserValuations: (userId) =>
    api.get(`/album/users/${userId}/valuations`),

  getQuickSwap: (otherUserId) =>
    api.get('/album/trades/quick-swap', { params: { with: otherUserId } }),

  // ── Trade contact (WhatsApp) ───────────────────────────────────
  // Devuelve {phone} sólo si el caller es proposer/receiver y el trade
  // está en ACCEPTED o COMPLETED. 404 si todavía no se puede compartir.
  getTradeContact: (tradeId) =>
    api.get(`/album/trades/${tradeId}/contact`, {
      validateStatus: s => s === 200 || s === 404,
    }),
};

export const activityService = {
  getRecentActivity: (limit = 20) =>
    api.get('/activity/recent', { params: { limit } }),
};

export default api;
